import { apiClient } from "lib";

export interface Workstation {
  id: string;
  name: string;
  status: "online" | "offline" | "error";
  lastSeenAt: string | null;
  facilityId: string | null;
  localPort: number;
  currentIpAddresses?: string[] | null;
  isStainerBridge?: boolean;
  deviceCount: number;
  onlineDeviceCount: number;
  createdAt: string;
  updatedAt?: string;
  activeSession?: {
    id: string;
    connectedAt: string;
    lastHeartbeatAt: string;
  } | null;
  devices?: Device[];
}

export interface Device {
  id: string;
  workstationId: string;
  name: string;
  deviceType:
    | "slide_printer"
    | "cassette_printer"
    | "document_scanner"
    | "sticker_printer"
    | "stainer";
  connectionType:
    | "usb"
    | "serial"
    | "network"
    | "windows_spooler"
    | "wia"
    | "twain"
    | "folder_watch"
    | "file_output";
  connectionAddress: string;
  status: "connected" | "disconnected" | "error" | "unknown";
  lastSeenAt: string | null;
  metadata: Record<string, any> | null;
  createdAt: string;
}

export interface BridgeCommand {
  id: string;
  deviceId: string;
  workstationId: string;
  commandType: string;
  payload: Record<string, any>;
  status:
    | "pending"
    | "sent"
    | "acknowledged"
    | "completed"
    | "failed"
    | "expired";
  dispatchedAt: string | null;
  acknowledgedAt: string | null;
  completedAt: string | null;
  result: Record<string, any> | null;
  errorMessage: string | null;
  expiresAt: string;
  createdAt: string;
}

export interface DeviceEvent {
  id: string;
  deviceId: string;
  workstationId: string;
  eventType: string;
  data: Record<string, any>;
  occurredAt: string;
  createdAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

const WORKSTATION_PAGE_SIZE = 100;

async function getAllWorkstations(): Promise<Workstation[]> {
  const firstPage = await apiClient.get<PaginatedResponse<Workstation>>(
    `/bridge/workstations`,
    { params: { page: 1, limit: WORKSTATION_PAGE_SIZE } }
  );
  const { data, total } = firstPage.data;
  const pageCount = Math.ceil(total / WORKSTATION_PAGE_SIZE);
  if (pageCount <= 1) return data;

  const remainingPages = Array.from(
    { length: pageCount - 1 },
    (_, index) => index + 2
  );
  const responses = await Promise.all(
    remainingPages.map((page) =>
      apiClient.get<PaginatedResponse<Workstation>>(`/bridge/workstations`, {
        params: { page, limit: WORKSTATION_PAGE_SIZE },
      })
    )
  );
  return data.concat(responses.flatMap((response) => response.data.data));
}

export const bridgeService = {
  // Workstations
  getWorkstations: (params?: {
    status?: string;
    facilityId?: string;
    page?: number;
    limit?: number;
  }) =>
    apiClient.get<PaginatedResponse<Workstation>>(`/bridge/workstations`, {
      params,
    }),

  getWorkstation: (id: string) =>
    apiClient.get<Workstation>(`/bridge/workstations/${id}`),

  getAllWorkstations,

  createWorkstation: (data: {
    name: string;
    facilityId?: string;
    localPort?: number;
  }) =>
    apiClient.post<Workstation & { apiKey: string }>(
      `/bridge/workstations`,
      data
    ),

  regenerateKey: (id: string) =>
    apiClient.post<{ apiKey: string }>(
      `/bridge/workstations/${id}/regenerate-key`
    ),

  // Devices
  getDevices: (
    workstationId: string,
    params?: { status?: string; deviceType?: string }
  ) =>
    apiClient.get<{ data: Device[]; total: number }>(
      `/bridge/workstations/${workstationId}/devices`,
      { params }
    ),

  createDevice: (workstationId: string, data: Partial<Device>) =>
    apiClient.post<Device>(
      `/bridge/workstations/${workstationId}/devices`,
      data
    ),

  updateDevice: (
    workstationId: string,
    deviceId: string,
    data: Partial<Device>
  ) =>
    apiClient.put<Device>(
      `/bridge/workstations/${workstationId}/devices/${deviceId}`,
      data
    ),

  deleteDevice: (workstationId: string, deviceId: string) =>
    apiClient.delete(
      `/bridge/workstations/${workstationId}/devices/${deviceId}`
    ),

  deleteWorkstation: (id: string) =>
    apiClient.delete(`/bridge/workstations/${id}`),

  // Designate (or clear) this workstation as the single Staining Hub — all
  // staining orders route to it.
  setStainerBridge: (id: string, enabled: boolean) =>
    apiClient.put<{ id: string; isStainerBridge: boolean }>(
      `/bridge/workstations/${id}/stainer`,
      { enabled }
    ),

  // Commands
  createCommand: (data: {
    deviceId: string;
    commandType: string;
    payload: Record<string, any>;
    expiresInSeconds?: number;
  }) => apiClient.post<BridgeCommand>(`/bridge/commands`, data),

  getCommand: (id: string) =>
    apiClient.get<BridgeCommand>(`/bridge/commands/${id}`),

  // Events
  getEvents: (
    workstationId: string,
    params?: {
      deviceId?: string;
      eventType?: string;
      since?: string;
      page?: number;
      limit?: number;
    }
  ) =>
    apiClient.get<PaginatedResponse<DeviceEvent>>(
      `/bridge/workstations/${workstationId}/events`,
      { params }
    ),
};
